import { assign, fromPromise, setup } from "xstate";
import type { Todo } from "@listto/powersync";

export type TodoDetailEvent =
  | { type: "TEXT_CHANGED"; text: string }
  | { type: "NOTE_CHANGED"; note: string }
  | { type: "TOGGLE_COMPLETED" }
  | { type: "TOGGLE_IMPORTANT" }
  | { type: "TOGGLE_MY_DAY" }
  | { type: "SET_DUE_DATE"; date: Date | null }
  | { type: "SET_RECURRENCE"; rule: string | null }
  | { type: "DELETE" }
  | { type: "RETRY_SAVE" }
  | { type: "SYNC_TODO"; todo: Todo | undefined };

export interface TodoDetailInput {
  id: string;
  initialListId?: string;
  todo?: Todo;
}

export interface TodoDetailContext {
  id: string;
  initialListId?: string;
  todo: Todo | undefined;
  localText: string;
  localNote: string;
  error: string | null;
}

function getToggleValue(current: number | undefined | null): number {
  return current ? 0 : 1;
}

// oxlint-disable-next-line complexity
function calculateTodoValues(context: TodoDetailContext, event: TodoDetailEvent): Partial<Todo> {
  const values: Partial<Todo> = {};
  const { localText, localNote, todo } = context;

  // Handle text and note changes
  if (event.type === "TEXT_CHANGED" || localText !== todo?.text) {
    values.text = localText;
  }
  if (event.type === "NOTE_CHANGED" || localNote !== todo?.note) {
    // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
    values.note = localNote ?? null;
  }

  // Handle toggle operations
  if (event.type === "TOGGLE_COMPLETED") {
    values.completed = getToggleValue(todo?.completed);
  }
  if (event.type === "TOGGLE_IMPORTANT") {
    values.important = getToggleValue(todo?.important);
  }
  if (event.type === "TOGGLE_MY_DAY") {
    values.my_day_date = todo?.my_day_date ? null : new Date().toISOString();
  }

  // Handle date and recurrence
  if (event.type === "SET_DUE_DATE") {
    values.due_date = event.date?.toISOString() ?? null;
  }
  if (event.type === "SET_RECURRENCE") {
    values.recurrence_rule = event.rule;
  }

  return values;
}

export const todoDetailMachine = setup({
  // Schema Definition Pattern for XState v5 type inference
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  types: {} as {
    context: TodoDetailContext;
    events: TodoDetailEvent;
    input: TodoDetailInput;
  },
  actors: {
    updateTodoActor: fromPromise(async (_x: { input: { id: string; values: Partial<Todo> } }) => {
      /** No op - must be provided by implementation */
    }),
    deleteTodoActor: fromPromise(async (_x: { input: { id: string } }) => {
      /** No op - must be provided by implementation */
    }),
    redirectActor: fromPromise(async (_x: { input: { todo: Todo } }) => {
      /** No op - must be provided by implementation */
    }),
  },
  actions: {
    updateLocalText: assign({
      localText: ({ event }) => (event.type === "TEXT_CHANGED" ? event.text : ""),
    }),
    updateLocalNote: assign({
      localNote: ({ event }) => (event.type === "NOTE_CHANGED" ? event.note : ""),
    }),
    syncTodo: assign(({ context, event }) => {
      if (event.type !== "SYNC_TODO") {
        return {};
      }
      const { todo } = event;
      const patch: Partial<TodoDetailContext> = { todo };

      // If we didn't have a todo before (loading), initialize local text
      if (!context.todo && todo) {
        patch.localText = todo.text ?? "";
        patch.localNote = todo.note ?? "";
      }
      return patch;
    }),
  },
  guards: {
    isNonCanonical: ({ context, event }) => {
      if (event.type !== "SYNC_TODO" || !event.todo || !context.initialListId) {
        return false;
      }
      const { todo } = event;
      const currentListId = context.initialListId;

      // Logic from useTodoNavigation.isCanonical
      const isSmartSlug = ["all", "important", "my-day", "planned", "inbox"].includes(
        currentListId,
      );
      if (isSmartSlug) {
        return false;
      }

      return todo.list_id !== currentListId;
    },
  },
}).createMachine({
  id: "todoDetail",
  initial: "idle",
  context: ({ input }) => ({
    id: input.id,
    initialListId: input.initialListId,
    todo: input.todo,
    localText: input.todo?.text ?? "",
    localNote: input.todo?.note ?? "",
    error: null,
  }),
  states: {
    idle: {
      on: {
        TEXT_CHANGED: {
          target: "debouncingText",
          actions: "updateLocalText",
        },
        NOTE_CHANGED: {
          target: "debouncingNote",
          actions: "updateLocalNote",
        },
        TOGGLE_COMPLETED: {
          target: "saving",
        },
        TOGGLE_IMPORTANT: {
          target: "saving",
        },
        TOGGLE_MY_DAY: {
          target: "saving",
        },
        SET_DUE_DATE: {
          target: "saving",
        },
        SET_RECURRENCE: {
          target: "saving",
        },
        DELETE: "deleting",
        SYNC_TODO: [
          {
            target: "redirecting",
            guard: "isNonCanonical",
            actions: "syncTodo",
          },
          {
            actions: "syncTodo",
          },
        ],
      },
    },
    redirecting: {
      invoke: {
        src: "redirectActor",
        input: ({ context }) => {
          if (!context.todo) {
            throw new Error("No todo for redirection");
          }
          return { todo: context.todo };
        },
        onDone: "idle",
      },
    },
    debouncingText: {
      after: {
        600: "saving",
      },
      on: {
        TEXT_CHANGED: {
          target: "debouncingText",
          actions: "updateLocalText",
          reenter: true,
        },
        SYNC_TODO: {
          actions: "syncTodo",
        },
      },
    },
    debouncingNote: {
      after: {
        600: "saving",
      },
      on: {
        NOTE_CHANGED: {
          target: "debouncingNote",
          actions: "updateLocalNote",
          reenter: true,
        },
        SYNC_TODO: {
          actions: "syncTodo",
        },
      },
    },
    saving: {
      invoke: {
        src: "updateTodoActor",
        input: ({ context, event }) => ({
          id: context.id,
          values: calculateTodoValues(context, event),
        }),
        onDone: "idle",
        onError: {
          target: "error",
          actions: assign({
            error: ({ event }) =>
              event.error instanceof Error ? event.error.message : String(event.error),
          }),
        },
      },
    },
    deleting: {
      invoke: {
        src: "deleteTodoActor",
        input: ({ context }) => ({ id: context.id }),
        onDone: "idle", // Navigation handled outside
        onError: "error",
      },
    },
    error: {
      on: {
        RETRY_SAVE: "saving",
        TEXT_CHANGED: {
          target: "debouncingText",
          actions: "updateLocalText",
        },
        NOTE_CHANGED: {
          target: "debouncingNote",
          actions: "updateLocalNote",
        },
      },
    },
  },
});
